import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ModuleShell, Kpi, DataTable, StatusBadge } from "@/components/partners/common";
import { KIND, fmtMoney, fmtNum, titleCase, apiError } from "@/lib/partnerApi";

function Panel({ title, children, action }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-700">{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Bar({ label, value, max, tone = "bg-blue-500", right }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <div className="w-40 truncate text-slate-700" title={label}>{label}</div>
      <div className="flex-1 bg-slate-100 rounded h-2.5 overflow-hidden">
        <div className={`${tone} h-full rounded`} style={{ width: `${max ? Math.min(100, (100 * value) / max) : 0}%` }} />
      </div>
      <div className="w-28 text-right mono text-slate-600">{right}</div>
    </div>
  );
}

export default function PartnerDashboard({ kind }) {
  const k = KIND[kind];
  const nav = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`${k.api}/dashboard`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))); }, [k.api]);
  if (!d) return <ModuleShell kind={kind} title={`${k.label} Dashboard`}><div className="text-sm text-slate-500">Loading…</div></ModuleShell>;
  const c = d.counts;
  const go = (path) => () => nav(`${k.base}${path}`);

  return (
    <ModuleShell kind={kind} title={`${k.label} Dashboard`} tid={`${kind}-dashboard`}>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Kpi label={`Active ${k.plural}`} value={c.active} tone="text-emerald-700" onClick={go("")} tid="kpi-active" />
        {kind === "supplier" ? <Kpi label="Inactive / Blocked" value={`${c.inactive} / ${c.blocked}`} sub={`${c.under_review} under review`} />
          : <Kpi label="New Dealers (30d)" value={d.new_dealers} sub={`${c.under_review} under review`} />}
        <Kpi label="Pending Approvals" value={d.pending_approvals} tone={d.pending_approvals ? "text-amber-600" : "text-slate-900"} onClick={go("/approvals")} tid="kpi-pending" />
        <Kpi label="Expiring Documents" value={d.expiring_documents_count} tone={d.expiring_documents_count ? "text-red-600" : "text-slate-900"} onClick={go("/alerts")} />
        {kind === "supplier" ? (
          <>
            <Kpi label="Total Purchase Value" value={d.financials_visible ? fmtMoney(d.total_purchase_value) : "—"} />
            <Kpi label="Outstanding Payables" value={d.financials_visible ? fmtMoney(d.outstanding_payables) : "—"} sub={d.financials_visible && d.overdue_payables ? `${fmtMoney(d.overdue_payables)} overdue` : ""} />
            <Kpi label="Late Deliveries" value={d.late_deliveries_count} tone={d.late_deliveries_count ? "text-red-600" : "text-slate-900"} />
            <Kpi label="Quality Rejections" value={d.quality_rejections_count} sub="last 30 days" tone={d.quality_rejections_count ? "text-red-600" : "text-slate-900"} />
            <Kpi label="Items Below Reorder" value={d.items_below_reorder_count} onClick={go("/procurement-plan")} />
            <Kpi label="Items with Preferred Supplier" value={d.preferred_suppliers_count} sub={`${d.raw_items_without_supplier} raw items unmapped`} />
          </>
        ) : (
          <>
            <Kpi label="Total Sales" value={d.financials_visible ? fmtMoney(d.total_sales) : "—"} />
            <Kpi label="Sales This Month" value={d.financials_visible ? fmtMoney(d.monthly_sales) : "—"} />
            <Kpi label="Outstanding Receivables" value={d.financials_visible ? fmtMoney(d.outstanding_receivables) : "—"} />
            <Kpi label="Overdue Amount" value={d.financials_visible ? fmtMoney(d.overdue_amount) : "—"} tone={d.overdue_amount ? "text-red-600" : "text-slate-900"} />
            <Kpi label="Pending Sales Order" value={d.pending_sales_orders} sub={d.financials_visible ? fmtMoney(d.pending_sales_orders_value) : ""} />
            <Kpi label="Returns" value={d.financials_visible ? fmtMoney(d.returns_value) : "—"} />
            <Kpi label="Open Complaints" value={d.open_complaints} />
            <Kpi label="Dealer Stock (qty)" value={fmtNum(Object.values(d.dealer_stock || {}).reduce((a, b) => a + b, 0))}
              sub={Object.entries(d.dealer_stock || {}).map(([o, q]) => `${titleCase(o)} ${fmtNum(q)}`).join(" · ")} />
          </>
        )}
      </div>

      {kind === "supplier" ? (
        <div className="grid lg:grid-cols-2 gap-5">
          <Panel title="Top Suppliers by Purchase Value">
            <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-2">
              {d.top_suppliers.length === 0 && <div className="text-sm text-slate-500">No purchase orders yet.</div>}
              {d.top_suppliers.map((s) => <Bar key={s.id} label={`${s.code} · ${s.name}`} value={s.total_purchase_value} max={d.top_suppliers[0]?.total_purchase_value} right={d.financials_visible ? fmtMoney(s.total_purchase_value) : `${s.po_count} POs`} />)}
            </div>
          </Panel>
          <Panel title="Supplier Performance (lowest scores first)">
            <DataTable rows={d.supplier_performance} empty="No performance data yet." onRowClick={(r) => nav(`${k.base}/${r.id}`)} columns={[
              { key: "code", label: "Code" }, { key: "name", label: "Supplier" }, { key: "performance_score", label: "Score", type: "number" },
              { key: "on_time_delivery_pct", label: "On-Time %", type: "number" }, { key: "quality_acceptance_pct", label: "Quality %", type: "number" },
            ]} />
          </Panel>
          <Panel title="Late Deliveries (open POs past due)">
            <DataTable rows={d.late_deliveries} empty="No late deliveries." columns={[
              { key: "po_code", label: "PO" }, { key: "supplier_name", label: "Supplier" }, { key: "expected", label: "Expected", type: "date" },
              { key: "days_late", label: "Days Late", type: "number" },
            ]} />
          </Panel>
          <Panel title="Quality Rejections">
            <DataTable rows={d.quality_rejections} empty="No rejections in the look-back window." columns={[
              { key: "gin_code", label: "GIN" }, { key: "supplier_name", label: "Supplier" }, { key: "rejected_qty", label: "Rejected", type: "number" },
              { key: "date", label: "Date", type: "date" },
            ]} />
          </Panel>
          <Panel title="Items Below Reorder Level">
            <DataTable rows={d.items_below_reorder} empty="All items above minimum stock." columns={[
              { key: "item_code", label: "Item" }, { key: "item_name", label: "Description" }, { key: "available_qty", label: "Available", type: "number" },
              { key: "min_stock_level", label: "Minimum", type: "number" }, { key: "preferred_supplier", label: "Preferred Supplier" },
            ]} />
          </Panel>
          <Panel title="Expiring / Expired Documents">
            <DataTable rows={d.expiring_documents} empty="No documents expiring soon." onRowClick={(r) => nav(`${k.base}/${r.partner_id}`)} columns={[
              { key: "partner_code", label: "Code" }, { key: "partner_name", label: "Supplier" }, { key: "doc_type", label: "Document" },
              { key: "expiry_date", label: "Expiry", type: "date" }, { key: "state", label: "State", render: (r) => <StatusBadge value={r.state} /> },
            ]} />
          </Panel>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-5">
          <Panel title="Top Dealers by Sales">
            <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-2">
              {d.top_dealers.length === 0 && <div className="text-sm text-slate-500">No dealers yet.</div>}
              {d.top_dealers.map((x) => <Bar key={x.id} label={`${x.code} · ${x.name}`} value={x.sales || 0} max={d.top_dealers[0]?.sales} right={d.financials_visible ? fmtMoney(x.sales) : ""} />)}
            </div>
          </Panel>
          <Panel title="Sales Target vs Achievement (this month)">
            <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-2">
              {d.target_vs_achievement.length === 0 && <div className="text-sm text-slate-500">No targets set for this month.</div>}
              {d.target_vs_achievement.map((t) => <Bar key={t.code} label={`${t.code} · ${t.name}`} value={t.achieved} max={t.target}
                tone={t.pct >= 100 ? "bg-emerald-500" : t.pct >= 70 ? "bg-amber-500" : "bg-red-500"} right={`${fmtNum(t.pct, 0)}% of ${fmtMoney(t.target)}`} />)}
            </div>
          </Panel>
          {d.aging && (
            <Panel title="Receivables Aging">
              <div className="grid grid-cols-5 gap-2">
                {Object.entries(d.aging).map(([b, v]) => (
                  <div key={b} className={`rounded-md border p-3 ${b !== "current" && v > 0 ? "border-red-200 bg-red-50" : "border-slate-200 bg-white"}`}>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">{{ current: "Current", d1_30: "1–30", d31_60: "31–60", d61_90: "61–90", d90_plus: "90+" }[b]}</div>
                    <div className="text-sm font-bold mono mt-1">{fmtMoney(v)}</div>
                  </div>
                ))}
              </div>
            </Panel>
          )}
          <Panel title="Credit Limit Exceeded">
            <DataTable rows={d.credit_exceeded} empty="No dealer is over its credit limit." columns={[
              { key: "code", label: "Code" }, { key: "name", label: "Dealer" }, { key: "limit", label: "Limit", type: "money" }, { key: "used", label: "Used", type: "money" },
            ]} />
          </Panel>
          <Panel title="Expiring / Expired Documents">
            <DataTable rows={d.expiring_documents} empty="No documents expiring soon." onRowClick={(r) => nav(`${k.base}/${r.partner_id}`)} columns={[
              { key: "partner_code", label: "Code" }, { key: "partner_name", label: "Dealer" }, { key: "doc_type", label: "Document" },
              { key: "expiry_date", label: "Expiry", type: "date" }, { key: "state", label: "State", render: (r) => <StatusBadge value={r.state} /> },
            ]} />
          </Panel>
        </div>
      )}
    </ModuleShell>
  );
}
